import { LocalStorageUtils } from './LocalStorageUtils';
import { registedDomains } from './RegisteredDomains';
import { isAllowed } from '../cmp/utils';
import translations from '../resources/translations/translations';
import { hiddenBannerLocales } from '../cmp/constants';

const DIDOMI_TOKEN_COOKIE_NAME = 'euconsent-v2';
const DIDOMI_TOKEN_COOKIE_NAME_QA = 'euconsent';
const USER_STATUS_ITEM_NAME = 'didomi_user_status';
const CURRENT_USER_STATUS_ITEM_NAME = 'didomi_current_user_status';
const USER_CONSENT_CHANGED_EVENT = 'DataMesh:UserConsentChanged';
export const EDO_VENDOR = 'eDo';
export const TECHNICAL_PURPOSE = 'technical';
export const DEFAULT_DOMAIN = 'sdk.privacy-center.org';

// status.purposes/status.vendors are maps of id -> { enabled } as returned by Didomi.getCurrentUserStatus()
const getEnabledIds = statusMap =>
  Object.keys(statusMap || {}).filter(id => statusMap[id]?.enabled === true);

const isWindowDefined = () => typeof window !== 'undefined';

export const Websites = Object.freeze({
  EDREAMS: 'ED',
  OPODO: 'OP',
  GOVOYAGES: 'GO',
  TRAVELLINK: 'TR',
});

export const DidomiUtils = {
  consentChangeCallbacks: new Map(),
  onChangeCallback: () => {
    while (global.CMPCallbackList && global.CMPCallbackList.length > 0) {
      const cb = global.CMPCallbackList.pop();
      cb({ isAllowed });
    }
  },
  getInfo() {
    return this.isDebugMode()
      ? require('../resources/qa/storageConsentInfoStrict')
      : require('../resources/prod/storageConsentInfoStrict');
  },
  getValuesInPropertiesFromKey(storage, key) {
    let vendor = '';
    let purposes = [];
    const hardCodedValues = this.getInfo();
    if (hardCodedValues) {
      const item = hardCodedValues[storage][key];
      if (item) {
        vendor = item.vendor;
        purposes = item.purposes;
      }
    }
    return { vendor, purposes };
  },
  isPurposesAllowed(purposes, status) {
    if (!purposes || purposes.length === 0) {
      return true;
    }

    const purposesMap = status && status.purposes ? status.purposes : {};

    const isPurposeEnabled = purpose => {
      const purposeStatus = purposesMap[purpose];
      if (!purposeStatus) return false;
      return purposeStatus.enabled === true;
    };

    return purposes.every(isPurposeEnabled);
  },
  isVendorAllowed(vendor, status) {
    if (!vendor || vendor === EDO_VENDOR) {
      return true;
    }

    const vendorsMap = status && status.vendors ? status.vendors : {};
    const vendorStatus = vendorsMap[vendor];

    if (!vendorStatus) return false;
    return vendorStatus.enabled === true;
  },
  getDidomiTokenCookieName() {
    return this.isDebugMode()
      ? DIDOMI_TOKEN_COOKIE_NAME_QA
      : DIDOMI_TOKEN_COOKIE_NAME;
  },
  isDidomiTokenCookieSet() {
    const cookieName = this.getDidomiTokenCookieName();
    return window.document.cookie.indexOf(cookieName) !== -1;
  },
  /**
   * @deprecated Use getCurrentUserStatusFromDidomi instead
   */
  getUserStatusFromDidomi() {
    return window.Didomi?.getUserStatus?.();
  },
  getCurrentUserStatusFromDidomi() {
    return window.Didomi?.getCurrentUserStatus?.();
  },
  dispatchUserConsentDomainEvent(moduleName, moduleVersion) {
    if (!isWindowDefined()) {
      return;
    }

    const status = this.getCurrentUserStatusFromDidomi();
    if (!status) {
      return;
    }

    const detail = {
      sourceFrontendModule: moduleName || 'UNKNOWN',
      sourceFrontendModuleVersion: moduleVersion || 'UNKNOWN',
      regulationName: status.regulation?.toUpperCase() || 'UNKNOWN',
      purposesConsent: getEnabledIds(status.purposes),
      vendorsConsent: getEnabledIds(status.vendors),
    };

    if (typeof document === 'undefined') {
      return;
    }

    const target = document.body || document;
    target.dispatchEvent(
      new CustomEvent(USER_CONSENT_CHANGED_EVENT, { detail })
    );
  },
  saveUserStatus() {
    const status = this.getUserStatusFromDidomi();
    const currentStatus = this.getCurrentUserStatusFromDidomi();

    LocalStorageUtils.erase(USER_STATUS_ITEM_NAME);
    LocalStorageUtils.create(USER_STATUS_ITEM_NAME, status);

    LocalStorageUtils.erase(CURRENT_USER_STATUS_ITEM_NAME);
    LocalStorageUtils.create(CURRENT_USER_STATUS_ITEM_NAME, currentStatus);
  },
  getUserStatus() {
    const currentUserStatusItem = LocalStorageUtils.read(
      CURRENT_USER_STATUS_ITEM_NAME
    );
    let status;

    if (!this.isDidomiTokenCookieSet() || !currentUserStatusItem) {
      status = this.getCurrentUserStatusFromDidomi();
    } else {
      status = currentUserStatusItem;
    }

    return status;
  },
  loadDidomi() {
    if (DidomiUtils.isAlreadyInitialized()) {
      global.initCMPPromise = null;
      return Promise.resolve();
    }
    if (global.initCMPPromise) return global.initCMPPromise;
    const loadingPromise = DidomiUtils.loadDidomiScript();
    if (!loadingPromise) return loadingPromise;
    global.initCMPPromise = loadingPromise.finally(() => {
      global.initCMPPromise = null;
    });
    return global.initCMPPromise;
  },
  loadDidomiScript() {
    if (!isWindowDefined()) return;

    window.gdprAppliesGlobally = true;
    return new Promise(res => {
      (function() {
        (function(e) {
          const r = document.createElement('link');
          r.rel = 'preconnect';
          r.as = 'script';
          const t = document.createElement('link');
          t.rel = 'dns-prefetch';
          t.as = 'script';
          const n = document.createElement('script');
          n.onload = () => res();
          n.id = 'spcloader';
          n.type = 'text/javascript';
          n['async'] = true;
          n.charset = 'utf-8';
          let o =
            DidomiUtils.getDidomiUrl() +
            e +
            '/loader.js?target=' +
            document.location.hostname;
          if (window.didomiConfig && window.didomiConfig.user) {
            const i = window.didomiConfig.user;
            const a = i.country;
            const c = i.region;
            if (a) {
              o = o + '&country=' + a;
              if (c) {
                o = o + '&region=' + c;
              }
            }
          }
          r.href = DidomiUtils.getDidomiUrl();
          t.href = DidomiUtils.getDidomiUrl();
          n.src = o;
          const d = document.getElementsByTagName('script')[0];
          if (d) {
            d.parentNode.insertBefore(r, d);
            d.parentNode.insertBefore(t, d);
            d.parentNode.insertBefore(n, d);
          }
        })('c81d422d-223b-4c8a-8b2e-099d6fa3acb9');
      })();
    });
  },
  setDidomiEvents(locale, moduleName, moduleVersion) {
    if (!isWindowDefined()) {
      return;
    }
    const didomiShouldBeHidden = hiddenBannerLocales.has(locale);
    window.didomiEventListeners = window.didomiEventListeners || [];
    window.didomiEventListeners.push({
      event: 'consent.changed',
      listener: () => {
        // consent configuration has changed because the user has interacted with the popup and agreed/disagreed/configured&saved
        DidomiUtils.saveUserStatus();
        DidomiUtils.onChangeCallback();
        DidomiUtils.dispatchUserConsentDomainEvent(moduleName, moduleVersion);
        for (const [, cb] of this.consentChangeCallbacks) {
          cb();
        }
      },
    });
    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(() => {
      if (DidomiUtils.isDidomiTokenCookieSet()) {
        // consent is loaded but not shown as it was previously agreed/disagreed/configured&saved
        DidomiUtils.onChangeCallback();
      }
    });
    window.didomiOnReady.push(DidomiUtils.checkWebViewConsent);
    if (!didomiShouldBeHidden) {
      return;
    }
    window.didomiOnReady.push(DidomiUtils.agreeToAllIfBannerHidden);
  },
  checkWebViewConsent(didomi = window.Didomi) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('didomiConfig.notice.enable') === 'false') {
      didomi.setUserDisagreeToAll();
      didomi.notice.hide();
    }
  },
  agreeToAllIfBannerHidden(didomi = window.Didomi) {
    if (didomi.shouldConsentBeCollected()) {
      didomi.setUserAgreeToAll();
    }
  },
  getTranslationFile(website, locale) {
    return translations[`${website}_${locale}`];
  },
  getDidomiUrl() {
    if (!window?.didomiConfig?.sdkPath) {
      return `https://${DEFAULT_DOMAIN}/`;
    }
    return window.didomiConfig.sdkPath;
  },
  getDidomiDomain() {
    try {
      const hostname = window?.location?.hostname || '';
      const replacedHostname = hostname.replace(
        /([a-zA-Z0-9-.]+\.)?(edreams|opodo|travellink|govoyages)\.([a-zA-Z.]+)/,
        'privacy.$2.$3'
      );
      return registedDomains.includes(replacedHostname)
        ? replacedHostname
        : null;
    } catch {
      return null;
    }
  },
  setDidomiConfig(website, locale) {
    const translation = this.getTranslationFile(website, locale);
    const language = locale ? locale.substring(0, 2) : 'en';
    if (!isWindowDefined()) {
      return;
    }

    const didomiShouldBeHidden = hiddenBannerLocales.has(locale);
    const isEnabled =
      window.didomiConfig?.notice?.enable ?? !didomiShouldBeHidden;

    const customDomain = this.getDidomiDomain();
    window.didomiConfig = {
      ...window.didomiConfig,
      ...(customDomain && {
        sdkPath: `https://${customDomain}/`,
        apiPath: `https://${customDomain}/api`,
      }),
      app: {
        ...window.didomiConfig?.app,
        privacyPolicyURL: translation.cookiesPolicyLink,
      },
      languages: {
        ...window.didomiConfig?.languages,
        enabled: [language],
        default: language,
      },
      notice: {
        ...window.didomiConfig?.notice,
        enable: isEnabled,
      },
      user: {
        externalConsent: {
          enabled: true,
        },
      },
    };
    if (website === Websites.TRAVELLINK) {
      window.didomiConfig.user = {
        bots: {
          consentRequired: false,
          extraUserAgents: [
            'Mozilla\\/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit\\/537.36 (KHTML, like Gecko) Chrome\\/62.0.3202.94 Safari\\/537.36 Agnostik\\/1.0',
            'Mozilla\\/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit\\/537.36 (KHTML, like Gecko) Chrome\\/103.0.0.0 Safari\\/537.36 Agnostik\\/1.0',
            'Mozilla\\/5.0 (X11; CrOS x86_64 14092.57.0) AppleWebKit\\/537.36 (KHTML, like Gecko) Chrome\\/93.0.4577.85 Safari\\/537.36',
          ],
        },
      };
    }
  },
  setCallback(cb) {
    global.CMPCallbackList = global.CMPCallbackList || [];
    global.CMPCallbackList.push(cb);
  },
  isDebugMode() {
    return global.isCMPDebugMode ? global.isCMPDebugMode : false;
  },
  isAlreadyInitialized() {
    return isWindowDefined() ? !!window.Didomi : false;
  },
  // true while loadDidomiScript() is pending, before window.Didomi becomes available
  isInitializing() {
    return !!global.initCMPPromise;
  },
};
